/**
 * Le TYPE d'un critère de comparaison — chunk 9.AJ.1.
 *
 * ## Pourquoi ce typage a changé de workspace
 *
 * Il vivait dans `apps/web/.../comparisonFormat.ts`, où il ne servait qu'à FORMATER
 * une cellule. Le scoring en a besoin pour NORMALISER : on ne normalise pas un
 * pourcentage comme un montant, ni une date comme un multiple de salaire.
 *
 * S'il était resté côté navigateur, le serveur aurait dû s'en donner un second — et
 * le score et la valeur affichée auraient fini par ne plus parler du même champ.
 * C'est la décision PO déjà appliquée à `field-registry.ts` : la classification doit
 * venir d'un registre shared authoritative, pas d'une liste divergente cachée dans le
 * composant React.
 *
 * Déplacement PUR : aucune règle modifiée, aucun seuil touché. `comparisonFormat.ts`
 * réexporte ces symboles, donc aucun appelant ne change et les tests 9.S.1 couvrent
 * exactement les mêmes cas qu'avant.
 */

/**
 * Kind de champ pour le rendu cellule normalisé. `text` = fallback strict
 * (rendu inchangé via `formatScalar` + unit séparé, comportement 9.Q.1).
 */
export type FieldKind =
  | "currency_cad"
  | "percent"
  | "number_multiple"
  | "number"
  | "date_iso"
  | "text";

/**
 * Allowlist exact-match par `field_key` canonique. Vérifié contre l'i18n
 * catalogue `comparison.field_labels` + tests intégration API.
 */
const EXACT_KIND_MAP: Readonly<Record<string, FieldKind>> = {
  // Currency CAD — primes monétaires.
  prime_mensuelle: "currency_cad",
  prime_totale_mensuelle_base: "currency_cad",
  prime_totale_mensuelle_ild: "currency_cad",
  // Percent — uncertainty stockée en 0..1.
  uncertainty: "percent",
  // Date ISO.
  date_document: "date_iso",
  date_effet: "date_iso",
};

/**
 * Suffixes/prefixes par convention de nommage. L'ordre du `find()` est
 * déterministe (exact match > suffix > prefix > fallback).
 */
const SUFFIX_KIND_RULES: ReadonlyArray<{ suffix: string; kind: FieldKind }> = [
  { suffix: "_premium", kind: "currency_cad" },
  { suffix: "_amount", kind: "currency_cad" },
  { suffix: "_deductible", kind: "currency_cad" },
  { suffix: "_limit", kind: "currency_cad" },
  { suffix: "_rate", kind: "percent" },
  { suffix: "_percent", kind: "percent" },
  { suffix: "_percentage", kind: "percent" },
  { suffix: ".salary_multiple", kind: "number_multiple" },
  { suffix: "_count", kind: "number" },
  { suffix: "_year", kind: "number" },
];

const PREFIX_KIND_RULES: ReadonlyArray<{ prefix: string; kind: FieldKind }> = [
  { prefix: "prime_", kind: "currency_cad" },
  { prefix: "date_", kind: "date_iso" },
];

/**
 * Dispatch unit API ($ / % / x / variantes) → FieldKind. Prend priorité sur
 * le registry field_key quand `unit` non vide. Anti-doublon `$5,000.00 $`.
 */
const UNIT_KIND_MAP: Readonly<Record<string, FieldKind>> = {
  $: "currency_cad",
  CAD: "currency_cad",
  $CAD: "currency_cad",
  $CA: "currency_cad",
  "$ CAD": "currency_cad",
  "%": "percent",
  x: "number_multiple",
  "×": "number_multiple",
};

/**
 * Classifie un `field_key` via registry + naming rules. Fallback `text`.
 *   - exact match > suffix > prefix > "text"
 */
export function fieldKind(fieldKey: string): FieldKind {
  if (!fieldKey) return "text";
  const exact = EXACT_KIND_MAP[fieldKey];
  if (exact) return exact;
  for (const rule of SUFFIX_KIND_RULES) {
    if (fieldKey.endsWith(rule.suffix)) return rule.kind;
  }
  for (const rule of PREFIX_KIND_RULES) {
    if (fieldKey.startsWith(rule.prefix)) return rule.kind;
  }
  return "text";
}

/**
 * Résolution finale du kind effectif pour le rendu d'une cellule. Dispatch
 * primaire sur `unit` API si présent (vérité serveur), fallback registry
 * field_key sinon. Permet d'override `prime_xxx` text si unit=`%` arrive.
 */
export function resolveCellKind(fieldKey: string, unit: string | null | undefined): FieldKind {
  if (unit) {
    const trimmed = unit.trim();
    const byUnit = UNIT_KIND_MAP[trimmed];
    if (byUnit) return byUnit;
  }
  return fieldKind(fieldKey);
}

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return parseLocalizedNumericString(value);
  return null;
}

/**
 * Parse strict d'une string numérique localisée (FR-CA / EN-CA / OCR formaté).
 *
 * Accepte UNIQUEMENT :
 *   - "5969.66" / "5,969.66" / "5 969,66" / "5 969.66"
 *   - "5 969,66$" / "$5,969.66" / "5969,66 CAD" (markers $/CAD/$CA/$CAD)
 *   - Espaces ASCII / NBSP U+00A0 / NNBSP U+202F / THIN U+2009 comme grouping
 *   - Signe `-` en tête
 *
 * Refuse :
 *   - Strings avec lettres autres que les markers monétaires connus
 *   - Strings ambiguës `5,000` / `5.000` (1 séparateur unique + 3 chiffres
 *     après → impossible de trancher thousands vs decimal → null)
 *   - `%` jamais strippé (préserve la sémantique percent — le parseur reste
 *     côté valeur numérique pure, pas de double-conversion)
 *
 * Retourne `null` en cas d'échec → permet au caller de bascule fallback text.
 */
export function parseLocalizedNumericString(value: string): number | null {
  if (typeof value !== "string") return null;
  let s = value.trim();
  if (!s) return null;

  // Strip d'UN marker monétaire en prefix ou suffix (longest match d'abord).
  const MARKERS = ["$CAD", "$CA", "CAD", "$"];
  const lower = s.toLowerCase();
  for (const m of MARKERS) {
    const ml = m.toLowerCase();
    if (lower.startsWith(ml)) {
      s = s.slice(m.length);
      break;
    }
    if (lower.endsWith(ml)) {
      s = s.slice(0, s.length - m.length);
      break;
    }
  }
  s = s.trim();

  // Strip toutes les variantes d'espaces (grouping FR-CA narrow + ASCII).
  s = s.replace(/[\s\u00A0\u202F\u2009]/g, "");
  if (!s) return null;

  // Allowlist stricte des chars restants : digits + . + , + signe `-` en tête.
  if (!/^-?[\d.,]+$/.test(s)) return null;

  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  let decimalSep: "." | "," | null = null;
  let groupingSep: "." | "," | null = null;

  if (lastDot >= 0 && lastComma >= 0) {
    // Both present : last occurrence wins as decimal, l'autre = grouping.
    if (lastDot > lastComma) {
      decimalSep = ".";
      groupingSep = ",";
    } else {
      decimalSep = ",";
      groupingSep = ".";
    }
  } else if (lastDot >= 0 || lastComma >= 0) {
    const sep: "." | "," = lastDot >= 0 ? "." : ",";
    const sepIdx = lastDot >= 0 ? lastDot : lastComma;
    const after = s.length - sepIdx - 1;
    const count = (s.match(sep === "." ? /\./g : /,/g) ?? []).length;
    if (after === 3) {
      // Ambigu : 1 séparateur unique + 3 chiffres après → impossible de
      // trancher 1,000 (mille EN) vs 1,000 (un FR-CA). Si plusieurs
      // occurrences du même séparateur → grouping (5,000,000). Sinon reject.
      if (count > 1) {
        decimalSep = null;
        groupingSep = sep;
      } else {
        return null;
      }
    } else {
      decimalSep = sep;
    }
  }

  let cleaned = s;
  if (groupingSep) cleaned = cleaned.split(groupingSep).join("");
  if (decimalSep) cleaned = cleaned.replace(decimalSep, ".");
  if (
    !/^-?\d*\.?\d*$/.test(cleaned) ||
    cleaned === "" ||
    cleaned === "-" ||
    cleaned === "." ||
    cleaned === "-."
  ) {
    return null;
  }
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}
